import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * One row per OPTED-IN LLM task call made through `AnthropicTaskService` — the
 * verbatim record of what was sent and what came back.
 *
 * Why this exists next to `llm_task_usage`: the usage ledger answers "what did
 * it cost", and cannot answer "what did it actually do". A hosted web-search
 * call runs several server-side searches inside a single response, and the only
 * place that back-and-forth is ever visible is the response content blocks. Once
 * the call returns, that material is read for its forced-tool output and thrown
 * away, so an operator asking "which pages did this lookup read, and why did it
 * answer that" has nothing to look at.
 *
 *   - **The FAILED call is the important one.** A call that times out leaves no
 *     usage row (there is no usage block to read) and no result, so today it
 *     leaves no trace at all — exactly the call an operator most wants to see.
 *     `response` is nullable and `errorText` is populated for that case, so the
 *     request snapshot survives a call that never returned.
 *   - **Opt-in per call, not per service.** Transcripts are large. Every task
 *     call is ledgered; only the calls whose caller asked are transcribed.
 *   - **Not a conversation.** There is no loop here — one request, one response.
 *     The "back-and-forth" is entirely inside the response's block array, which
 *     is why the whole thing fits in one row rather than a message table.
 *
 * `auditable: false` — same rationale as `llm_task_usage`. This is telemetry
 * written as a side effect of a model call; an `_audit` row per insert would
 * double the write volume to record that a telemetry row was inserted.
 */
@Index('idx_llm_task_transcript_feature', ['feature'])
@Index('idx_llm_task_transcript_reference', ['referenceType', 'referenceId'])
@Entity('llm_task_transcript', {}, { auditable: false })
export class LlmTaskTranscriptEntity extends BaseEntity {
	/**
	 * Which part of the pipeline made the call, e.g. `external_lookup`. Mirrors
	 * `llm_task_usage.feature` so a transcript and its ledger row can be lined up
	 * on (feature, label, reference).
	 */
	@Column('varchar', { name: 'feature', length: 64 })
	public feature!: string;

	/** The task label the caller passed to `AnthropicTaskService`. */
	@Column('varchar', { name: 'label', length: 128 })
	public label!: string;

	/**
	 * The model the call REQUESTED, after the service resolved defaults. It is
	 * what the snapshot was sent to; the model that actually served the request
	 * may differ (an alias resolves at the API), and the response's own `model`
	 * field inside `response` is the authority on that.
	 */
	@Column('varchar', { name: 'model', length: 64 })
	public model!: string;

	/**
	 * Generic provenance, matching `llm_task_usage`: what the call was FOR, e.g.
	 * `cross_reference_request` + the request id. Deliberately not real foreign
	 * keys — the referent type varies by feature, and a transcript must survive
	 * the deletion of whatever caused it.
	 */
	@Column('varchar', { name: 'referenceType', length: 64, nullable: true })
	public referenceType!: string | null;

	@Column('int', { name: 'referenceId', unsigned: true, nullable: true })
	public referenceId!: number | null;

	/** What was sent: the system prompt, the content blocks, the forced output
	 * tool and any hosted server tools, captured BEFORE the call so it exists
	 * even when the call never returns. */
	@Column('json', { name: 'request' })
	public request!: LlmTaskTranscriptRequestModel;

	/**
	 * The raw response content blocks, in order — including every server-side
	 * tool use and tool result the model produced inside the turn. Null when the
	 * call threw before a response existed.
	 */
	@Column('json', { name: 'response', nullable: true })
	public response!: Array<unknown> | null;

	@Column('varchar', { name: 'stopReason', length: 32, nullable: true })
	public stopReason!: string | null;

	/** The thrown error's message, when the call never returned. */
	@Column('text', { name: 'errorText', nullable: true })
	public errorText!: string | null;

	/** Wall clock for the API call itself, including the time a timed-out call
	 * spent waiting. */
	@Column('int', { name: 'durationMs', unsigned: true, nullable: true })
	public durationMs!: number | null;
}

/** The request snapshot stored in `llm_task_transcript.request`. */
export interface LlmTaskTranscriptRequestModel {
	system: string;
	/** The content blocks, as sent. Serialised opaquely: this is a record of
	 * what was transmitted, not something read back as a typed value. */
	content: Array<unknown>;
	tool: {
		name: string;
		description: string;
		inputSchema: Record<string, unknown>;
	};
	/** The Anthropic-hosted tools enabled for the call; empty on a plain task. */
	serverTools: Array<unknown>;
}
